import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { referralAPI } from '../api/endpoints';
import { formatXPUNT, formatDate, copyToClipboard } from '../utils/helpers';
import { 
  FaArrowLeft, FaUsers, FaGift, FaCopy, FaCheck,
  FaDollarSign, FaChartLine, FaShare, FaTwitter,
  FaWhatsapp, FaTelegram, FaFacebook, FaEnvelope,
  FaUserPlus, FaCrown, FaStar, FaTrophy,
  FaPercentage, FaWallet, FaHistory
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './ReferralDashboard.css';

const ReferralDashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [referralData, setReferralData] = useState({
    referral_code: '',
    referral_link: '',
    total_referrals: 0,
    referral_earnings: 0,
    referred_users: [],
    referral_history: [],
  });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchReferralData();
  }, [isAuthenticated, navigate]);

  const fetchReferralData = async () => {
    setLoading(true);
    try {
      const response = await referralAPI.getStats();
      setReferralData(response.data);
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    copyToClipboard(referralData.referral_link);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 3000);
  };

  const shareOnSocial = (platform) => {
    const text = `🎯 Join me on XPunters - the ultimate fans to fans sports prediction marketplace! Use my referral link: ${referralData.referral_link}`;
    const url = encodeURIComponent(referralData.referral_link);
    const encodedText = encodeURIComponent(text);
    
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${url}`,
      whatsapp: `https://wa.me/?text=${encodedText} ${url}`,
      telegram: `https://t.me/share/url?url=${url}&text=${encodedText}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${encodedText}`,
      email: `mailto:?subject=Join XPunters&body=${encodedText} ${url}`,
    };

    window.open(shareUrls[platform], '_blank');
  };

  // Calculate admin fee earnings (20%)
  const adminFeePercentage = 20;

  if (loading) {
    return (
      <div className="referral-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="referral-dashboard">
      <button onClick={() => navigate(-1)} className="back-button">
        <FaArrowLeft /> Back
      </button>

      <div className="referral-header">
        <div className="header-content">
          <h1>
            <FaGift className="header-icon" /> Referral Program
          </h1>
          <p className="header-subtitle">
            Earn 20% of admin fees whenever your referrals win
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <FaUsers className="stat-icon" />
          <div className="stat-info">
            <span className="stat-value">{referralData.total_referrals || 0}</span>
            <span className="stat-label">Total Referrals</span>
          </div>
        </div>
        <div className="stat-card gold">
          <FaPercentage className="stat-icon" />
          <div className="stat-info">
            <span className="stat-value">{adminFeePercentage}%</span>
            <span className="stat-label">Admin Fee Share</span>
          </div>
        </div>
        <div className="stat-card green">
          <FaWallet className="stat-icon" />
          <div className="stat-info">
            <span className="stat-value">{formatXPUNT(referralData.referral_earnings || 0)}</span>
            <span className="stat-label">Total Earnings</span>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="how-it-works-card">
        <h3>💰 How Referral Earnings Work</h3>
        <div className="how-steps">
          <div className="how-step">
            <span className="step-num">1</span>
            <span>Share your referral link with friends</span>
          </div>
          <div className="how-step">
            <span className="step-num">2</span>
            <span>Friend joins and starts predicting</span>
          </div>
          <div className="how-step">
            <span className="step-num">3</span>
            <span>When they win, you earn 20% of admin fees</span>
          </div>
          <div className="how-step highlight">
            <span className="step-num">⚡</span>
            <span>Earnings are automatically credited to your wallet</span>
          </div>
        </div>
      </div>

      {/* Referral Link Section */}
      <div className="referral-link-section">
        <h3>Your Referral Link</h3>
        <div className="referral-link-box">
          <code className="referral-link">{referralData.referral_link}</code>
          <button onClick={handleCopyLink} className="copy-btn">
            {copied ? <FaCheck /> : <FaCopy />}
          </button>
        </div>
        <p className="referral-hint">
          Share this link with your friends and earn 20% of admin fees when they win!
        </p>

        {/* Share Buttons */}
        <div className="share-buttons">
          <button 
            onClick={() => shareOnSocial('twitter')} 
            className="share-btn twitter"
            title="Share on Twitter"
          >
            <FaTwitter />
          </button>
          <button 
            onClick={() => shareOnSocial('whatsapp')} 
            className="share-btn whatsapp"
            title="Share on WhatsApp"
          >
            <FaWhatsapp />
          </button>
          <button 
            onClick={() => shareOnSocial('telegram')} 
            className="share-btn telegram"
            title="Share on Telegram"
          >
            <FaTelegram />
          </button>
          <button 
            onClick={() => shareOnSocial('facebook')} 
            className="share-btn facebook"
            title="Share on Facebook"
          >
            <FaFacebook />
          </button>
          <button 
            onClick={() => shareOnSocial('email')} 
            className="share-btn email"
            title="Share via Email"
          >
            <FaEnvelope />
          </button>
        </div>
      </div>

      {/* Referral Benefits */}
      <div className="benefits-section">
        <h3>🎯 Referral Benefits</h3>
        <div className="benefits-grid">
          <div className="benefit-item">
            <div className="benefit-icon">💰</div>
            <div className="benefit-info">
              <h4>20% of Admin Fees</h4>
              <p>Earn 20% from every admin fee your referrals generate</p>
            </div>
          </div>
          <div className="benefit-item">
            <div className="benefit-icon">⚡</div>
            <div className="benefit-info">
              <h4>Automatic Payouts</h4>
              <p>No manual claims needed. Earnings go straight to your wallet</p>
            </div>
          </div>
          <div className="benefit-item">
            <div className="benefit-icon">📈</div>
            <div className="benefit-info">
              <h4>Unlimited Earnings</h4>
              <p>Refer as many friends as you want. No limits on earnings</p>
            </div>
          </div>
          <div className="benefit-item">
            <div className="benefit-icon">🏆</div>
            <div className="benefit-info">
              <h4>Passive Income</h4>
              <p>Keep earning as your friends continue to win predictions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Referred Users */}
      <div className="referred-users-section">
        <h3>
          <FaUsers /> Referred Users
        </h3>
        {referralData.referred_users?.length === 0 ? (
          <div className="empty-state">
            <p>No referrals yet. Share your link and start earning!</p>
          </div>
        ) : (
          <div className="users-table">
            <div className="table-header">
              <span>User</span>
              <span>Joined</span>
              <span>Status</span>
              <span>Earnings</span>
            </div>
            {referralData.referred_users.map((user) => (
              <div key={user.id} className="table-row">
                <span className="user-name">
                  <span className="user-avatar">
                    {user.name?.[0] || '?'}
                  </span>
                  {user.name}
                </span>
                <span className="user-date">{formatDate(user.joined)}</span>
                <span className="user-status active">✅ Active</span>
                <span className="user-earnings">{formatXPUNT(user.earnings || 0)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Referral History */}
      {referralData.referral_history?.length > 0 && (
        <div className="bonus-history-section">
          <h3>
            <FaHistory /> Referral Earnings History
          </h3>
          <div className="bonus-list">
            {referralData.referral_history.map((item) => (
              <div key={item.id} className="bonus-item">
                <div className="bonus-item-info">
                  <span className="bonus-type">
                    {item.type === 'referral_earning' ? '💰 Referral Earning' : item.type}
                  </span>
                  <span className="bonus-date">{formatDate(item.created_at)}</span>
                  {item.description && (
                    <span className="bonus-description">{item.description}</span>
                  )}
                </div>
                <span className="bonus-amount positive">
                  +{formatXPUNT(item.amount)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralDashboard;